import { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { getConsent, setConsent } from "@/lib/cookie-consent";
import { Button } from "@/components/ui/button";

type View = "banner" | "preferences" | "hidden";

export function CookieConsent() {
  const [view, setView] = useState<View>("hidden");
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!getConsent()) setView("banner");

    const openPrefs = () => {
      const existing = getConsent();
      setAnalytics(existing?.analytics ?? false);
      setMarketing(existing?.marketing ?? false);
      setView("preferences");
    };
    window.addEventListener("rt-open-cookie-preferences", openPrefs);
    return () => window.removeEventListener("rt-open-cookie-preferences", openPrefs);
  }, []);

  // While the notice is on screen it floats above the bottom of the screen, so
  // the page shell reserves room at its very end. Without this the notice sits
  // on top of the last button on the page (the October offer pay button).
  useEffect(() => {
    const root = document.documentElement;
    if (view === "banner") root.setAttribute("data-cookie-banner", "on");
    else root.removeAttribute("data-cookie-banner");
  }, [view]);

  if (view === "hidden") return null;

  const acceptAll = () => {
    setConsent({ analytics: true, marketing: true });
    setView("hidden");
  };
  const rejectNonEssential = () => {
    setConsent({ analytics: false, marketing: false });
    setView("hidden");
  };
  const savePreferences = () => {
    setConsent({ analytics, marketing });
    setView("hidden");
  };

  if (view === "preferences") {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-prefs-title"
        className="fixed inset-0 z-[100] flex items-center justify-center bg-foreground/40 px-4 py-8 backdrop-blur-sm"
      >
          <div className="max-h-[calc(100dvh-4rem)] w-full max-w-md overflow-y-auto rounded-2xl border border-border bg-card p-6 shadow-xl">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h2 id="cookie-prefs-title" className="font-serif text-xl text-foreground">
                Cookie preferences
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Manage what we store on your device. Essential items keep the
                site working and cannot be turned off.
              </p>
            </div>
            <button
              onClick={() => setView("hidden")}
              aria-label="Close"
              className="rounded-full p-1 text-muted-foreground hover:bg-accent"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <ul className="space-y-3 text-sm">
            <li className="rounded-lg border border-border p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-foreground">Essential</p>
                  <p className="text-xs text-muted-foreground">
                    Required for sign-in, security, and core functionality.
                  </p>
                </div>
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  Always on
                </span>
              </div>
            </li>
            <li className="rounded-lg border border-border p-3">
              <label className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-foreground">Analytics</p>
                  <p className="text-xs text-muted-foreground">
                    Helps us understand how the platform is used so we can improve it.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="h-4 w-4"
                />
              </label>
            </li>
            <li className="rounded-lg border border-border p-3">
              <label className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-foreground">Marketing</p>
                  <p className="text-xs text-muted-foreground">
                    Allows us to measure the reach of our updates and events.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className="h-4 w-4"
                />
              </label>
            </li>
          </ul>

          <div className="mt-5 flex flex-wrap justify-end gap-2">
            <button
              onClick={rejectNonEssential}
              className="rounded-lg border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
            >
              Reject non-essential
            </button>
            <button
              onClick={savePreferences}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Save preferences
            </button>
          </div>

          <p className="mt-4 text-center text-[11px] text-muted-foreground">
            See our{" "}
            <Link to="/cookies" className="underline hover:text-foreground">
              Cookie Policy
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="underline hover:text-foreground">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="rt-cookie-banner fixed inset-x-2 bottom-[calc(env(safe-area-inset-bottom)+76px)] z-[95] rounded-2xl border border-border bg-card/95 px-3 py-2.5 shadow-2xl backdrop-blur supports-[backdrop-filter]:bg-card/90 sm:inset-x-0 sm:bottom-0 sm:rounded-none sm:border-x-0 sm:border-b-0 sm:border-t sm:px-4 sm:py-4"
    >
      <div className="rt-cookie-layout mx-auto flex max-w-5xl items-center gap-2 sm:flex-col sm:gap-3 md:flex-row md:items-center md:justify-between">
        {/* Mobile: ultra-compact single row */}
        <div className="rt-cookie-compact-copy flex min-w-0 flex-1 items-center gap-2 sm:hidden">
          <Cookie className="h-4 w-4 shrink-0 text-[var(--gold-deep)]" />
          <p className="truncate text-xs text-foreground/85">
            We use cookies.{" "}
            <Link to="/cookies" className="underline">
              Learn more
            </Link>
          </p>
        </div>
        <div className="rt-cookie-compact-actions flex shrink-0 items-center gap-1.5 sm:hidden">
          <Button variant="outline"
            onClick={rejectNonEssential}
            className="inline-flex min-h-[44px] items-center rounded-full border border-input bg-background px-3.5 text-[11px] font-medium text-foreground"
            aria-label="Reject non-essential cookies"
          >
            Reject
          </Button>
          <Button
            onClick={acceptAll}
            className="inline-flex min-h-[44px] items-center rounded-full bg-primary px-4 text-[11px] font-medium text-primary-foreground"
            aria-label="Accept all cookies"
          >
            Accept
          </Button>
          <Button variant="ghost" size="icon"
            onClick={() => setView("preferences")}
            aria-label="Cookie preferences"
            className="inline-flex h-[44px] w-[44px] items-center justify-center rounded-full text-muted-foreground"
          >
            <Cookie className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Desktop / tablet: full banner */}
        <div className="rt-cookie-full hidden items-start gap-3 sm:flex">
          <Cookie className="mt-0.5 h-5 w-5 shrink-0 text-[var(--gold-deep)]" />
          <p className="text-sm leading-relaxed text-foreground/85">
            We use essential cookies to make Recovery Trauma™ work. With your
            permission we may also use analytics and marketing cookies.{" "}
            <Link to="/cookies" className="underline hover:text-foreground">
              Cookie Policy
            </Link>
            .
          </p>
        </div>
        <div className="rt-cookie-full hidden flex-wrap justify-end gap-2 sm:flex">
          <button
            onClick={() => setView("preferences")}
            className="rounded-lg border border-input bg-background px-3 py-2 text-xs font-medium text-foreground hover:bg-accent"
          >
            Manage preferences
          </button>
          <button
            onClick={rejectNonEssential}
            className="rounded-lg border border-input bg-background px-3 py-2 text-xs font-medium text-foreground hover:bg-accent"
          >
            Reject non-essential
          </button>
          <button
            onClick={acceptAll}
            className="rounded-lg bg-primary px-3 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
